import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import ProductDetailView from './ProductDetailView';

const PublicContentPage = ({ theme, toggleTheme }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const storeSlug = searchParams.get('store');
  const backPath = storeSlug ? `/creador/${storeSlug}` : '/dashboard';

  // ── Lee sesión del usuario si está logueado ──────────────────────────────
  // Extrae userId del JWT y verifica que no esté expirado
  const getSessionFromToken = () => {
    const tk = localStorage.getItem('token');
    if (!tk) return { token: null, userId: null };
    try {
      const payload = JSON.parse(atob(tk.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token'); // token expirado
        return { token: null, userId: null };
      }
      return {
        token: tk,
        userId: payload.userId || payload.id || payload._id || null,
      };
    } catch { return { token: null, userId: null }; }
  };

  const { token, userId } = getSessionFromToken();
  const isLoggedIn = !!token && !!userId;

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/content-detail/${id}`);
        setContent(res.data);
      } catch (err) {
        setError('Contenido no encontrado');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [id]);

  if (loading) return (
    <PageWrap active={theme === 'dark'}>
      <Loader><Spinner />Cargando contenido...</Loader>
    </PageWrap>
  );

  if (error) return (
    <PageWrap active={theme === 'dark'}>
      <ErrorBox>
        <h2>😕 {error}</h2>
          <BackBtn onClick={() => navigate(backPath)}>Ir al inicio</BackBtn>
      </ErrorBox>
    </PageWrap>
  );

  const showBanner = content?.isDigitalProduct && !isLoggedIn;

  return (
    <PageWrap active={theme === 'dark'}>
      {showBanner && (
        <LoginBanner>
          <span>🔐 Inicia sesión para descargar este contenido</span>
          <LoginBtn onClick={() => navigate('/login', { state: { redirectTo: `/content/${id}${storeSlug ? `?store=${storeSlug}` : ''}` } })}>
            Iniciar sesión
          </LoginBtn>
        </LoginBanner>
      )}

      {/* REEMPLAZA ESTO: Envuelve el ProductDetailView */}
      <DetailContainer hasBanner={showBanner}>
        <ProductDetailView
          content={content}
          theme={theme}
          token={token}
          images={{
            [content._id]: content.image?.startsWith('http')
              ? content.image
              : Array.isArray(content.file)
                ? content.file[0]
                : content.file
          }}
          userId={userId}
          isPublicPage={true}
          onBack={() => navigate(backPath)}
        />
      </DetailContainer>
    </PageWrap>
  );
};

const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;

const PageWrap = styled.div`
  min-height: 100vh;
  background-color: ${p => p.active ? '#000' : '#ffffff'};
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

// NUEVO: Envuelve el ProductDetailView con este para bajar el botón "Volver"
const DetailContainer = styled.div`
  width: 100%;
  padding-top: ${p => p.hasBanner ? '40px' : '60px'};
  @media (max-width: 768px) {
    padding-top: ${p => p.hasBanner ? '20px' : '40px'};
  }
`;

const LoginBanner = styled.div`
  width: 100%;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  margin-left: -15px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 16px;
    gap: 10px;
    /* Alineamos todo al centro pero con un ligero ajuste a la izquierda */
    align-items: center; 
    text-align: center;
    
    span {
      margin-left: -15px; /* Empuja el texto un poco a la izquierda */
      display: inline-block;
    }
  }
`;

const LoginBtn = styled.button`
  background: white;
  color: #4f46e5;
  border: none;
  padding: 8px 25px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    margin-left: -15px; /* Alinea el botón con el texto movido */
  }

  &:hover {
    background: #f0f0ff;
    transform: scale(1.05);
  }
`;


const Loader = styled.div`
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  width: 100%; height: 80vh; gap: 20px;
  font-size: 18px; font-weight: 600;
  color: #888;
`;

const Spinner = styled.div`
  width: 48px; height: 48px;
  border: 4px solid rgba(0,0,0,0.1);
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const ErrorBox = styled.div`
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  height: 80vh; gap: 20px;
  h2 { color: #888; }
`;

const BackBtn = styled.button`
  padding: 10px 24px; border-radius: 10px;
  background: #4f46e5; color: #fff;
  border: none; font-size: 14px; font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.85; }
`;

export default PublicContentPage;